import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper, faSpinner, faArrowUpRightFromSquare, faRotateRight, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { newsApi } from '../api/newsApi';
import './TickerNews.css';

const TickerNews = ({ symbol }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedArticles, setExpandedArticles] = useState(new Set());

  const loadNews = async (forceRefresh = false) => {
    if (!symbol) return;
    
    const symbolUpper = symbol.toUpperCase();
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch from database first (filtered by symbol)
      let response;
      try {
        response = await newsApi.getNews({ 
          limit: 8, 
          offset: 0,
          symbol: symbolUpper
        });
        const dbArticles = response.data || [];
        
        // If database has articles for this symbol, use them
        if (dbArticles.length > 0) {
          setArticles(dbArticles);
          setLoading(false);
          
          // If force refresh, also fetch fresh from Alpaca
          if (forceRefresh) {
            try {
              const freshResponse = await newsApi.fetchNews({ 
                limit: 8,
                symbols: symbolUpper,
                include_content: true
              });
              // Response structure: { success: true, data: [...] }
              // newsApi.fetchNews returns response.data from axios, which is { success: true, data: [...] }
              if (freshResponse && freshResponse.data && freshResponse.data.length > 0) {
                setArticles(freshResponse.data);
              }
            } catch (fetchErr) {
              console.warn('Failed to fetch fresh news:', fetchErr);
              // Continue with database articles
            }
          }
          return;
        }
      } catch (dbErr) {
        console.warn('Database fetch failed, trying direct Alpaca fetch:', dbErr);
      }
      
      // If database is empty or failed, fetch directly from Alpaca
      response = await newsApi.fetchNews({ 
        limit: 20,
        symbols: symbolUpper,
        include_content: true
      });
      // Response structure: { success: true, data: [...] }
      // newsApi.fetchNews returns response.data from axios, which is { success: true, data: [...] }
      const articles = (response && response.data) ? response.data : [];
      setArticles(articles);
    } catch (err) {
      console.error('Error loading news:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load news');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews(false);
  }, [symbol]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const toggleArticleExpansion = (articleId) => {
    setExpandedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const sanitizeHtml = (html) => {
    if (!html) return '';
    // Basic sanitization - remove script tags and dangerous attributes
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    
    // Remove script tags
    const scripts = tmp.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove style tags (optional, but safer)
    const styles = tmp.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    // Remove event handlers from all elements
    const allElements = tmp.querySelectorAll('*');
    allElements.forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    return tmp.innerHTML;
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faNewspaper} />
          {symbol} News
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadNews(true)}
            className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
            disabled={loading}
            title="Refresh now"
          >
            <FontAwesomeIcon icon={faRotateRight} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          {loading && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Loading
            </span>
          )}
        </div>
      </div>
      {error && (
        <div className="mb-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}
      {!loading && !error && articles.length === 0 && (
        <div className="text-xs text-gray-500">No news found for {symbol}.</div>
      )}
      <div className="space-y-2">
        {articles.map((article, idx) => {
          const articleId = article.id || article.alpacaId || `${article.url}-${idx}`;
          const isExpanded = expandedArticles.has(articleId);
          const hasContent = article.content && article.content.trim().length > 0;
          const contentText = hasContent ? stripHtml(article.content) : '';

          return (
            <div
              key={articleId}
              className="block border border-gray-200 rounded-lg p-2 hover:border-indigo-300 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-indigo-700 text-sm line-clamp-2 hover:underline"
                  >
                    {article.headline || article.title}
                  </a>
                  <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
                    {article.source && <span className="truncate">{article.source}</span>}
                    {(article.createdAt || article.publishedAt) && (
                      <span className="text-gray-400">
                        {formatDate(article.createdAt || article.publishedAt)}
                      </span>
                    )}
                  </div>
                  {article.summary && (
                    <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {article.summary}
                    </div>
                  )}
                  {hasContent && (
                    <div className="mt-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleArticleExpansion(articleId);
                        }}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        <FontAwesomeIcon 
                          icon={isExpanded ? faChevronUp : faChevronDown} 
                          className="text-[10px]"
                        />
                        {isExpanded ? 'Hide Content' : 'Show Content'}
                      </button>
                      {isExpanded && (
                        <div 
                          className="mt-2 text-sm text-gray-700 leading-relaxed max-h-96 overflow-y-auto p-4 bg-gray-50 rounded border border-gray-200 news-content"
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtml(article.content) 
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-400 hover:text-indigo-600 flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-xs" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TickerNews;
